import type { HTMLAttributes, ReactNode } from 'react';

import { HEALTH_LABEL, PROPOSAL_STATUS_LABEL } from '@/domain/health';
import type { CheckOutcome, Health, ProposalStatus, RunOutcome } from '@/domain/types';

import { useChangeCount } from './motion';

import {
  AlertCircle,
  Check,
  CheckCircle,
  Clock,
  Cross,
  CrossCircle,
  EyeOff,
  MinusCircle,
  Refresh,
  Warning,
  Wrench,
} from './icons';

/*
  One chip geometry for everything that is a status, a tag or an outcome: 20px tall, 4px
  corners, 12px text, a 12px icon. Three tiers share it: the Health chip (soft fill, ink, icon),
  the outcome code (uppercase, bold, filled only when it failed) and the neutral tag.
*/
const CHIP =
  'inline-flex h-5 items-center gap-1 rounded-sm px-1.5 text-meta font-medium whitespace-nowrap';
const CHIP_ICON = 12;

/** Status colours are reserved for Health and run outcomes, and always travel with an icon and a label. */
const HEALTH_STYLE: Record<
  Health,
  { soft: string; ink: string; dot: string; icon: (size: number) => ReactNode }
> = {
  ok: {
    soft: 'bg-status-ok-soft',
    ink: 'text-status-ok-ink',
    dot: 'bg-status-ok',
    icon: (s) => <CheckCircle size={s} />,
  },
  impaired: {
    soft: 'bg-status-impaired-soft',
    ink: 'text-status-impaired-ink',
    dot: 'bg-status-impaired',
    icon: (s) => <Warning size={s} />,
  },
  degraded: {
    soft: 'bg-status-degraded-soft',
    ink: 'text-status-degraded-ink',
    dot: 'bg-status-degraded',
    icon: (s) => <AlertCircle size={s} />,
  },
  failed: {
    soft: 'bg-status-failed-soft',
    ink: 'text-status-failed-ink',
    dot: 'bg-status-failed',
    icon: (s) => <CrossCircle size={s} />,
  },
  maintenance: {
    soft: 'bg-status-maintenance-soft',
    ink: 'text-status-maintenance-ink',
    dot: 'bg-status-maintenance',
    icon: (s) => <Wrench size={s} />,
  },
};

/**
 * A Health value. As a chip it has the shared geometry; as text the icon carries the colour and
 * the label stays regular weight, except at `lg` where it is the title of a card.
 */
export function HealthBadge({
  health,
  size = 'md',
  variant = 'chip',
  label,
  className,
}: {
  health: Health;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'chip' | 'text';
  label?: string;
  className?: string;
}) {
  const style = HEALTH_STYLE[health];
  const text = label ?? HEALTH_LABEL[health];
  if (variant === 'text') {
    const iconSize = size === 'sm' ? 13 : size === 'lg' ? 18 : 15;
    // The card title may wrap on a narrow card; inline uses stay on one line.
    return (
      <span
        className={[
          'inline-flex items-center gap-1.5',
          size === 'lg' ? 'font-medium' : 'whitespace-nowrap',
          style.ink,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {style.icon(iconSize)}
        {text}
      </span>
    );
  }
  return (
    <span className={[CHIP, style.soft, style.ink, className].filter(Boolean).join(' ')}>
      {style.icon(CHIP_ICON)}
      {text}
    </span>
  );
}

export function HealthDot({ health, className }: { health: Health; className?: string }) {
  return (
    <span
      className={['inline-block size-2 rounded-full shrink-0', HEALTH_STYLE[health].dot, className]
        .filter(Boolean)
        .join(' ')}
      aria-hidden="true"
    />
  );
}

/*
  A run or check outcome, as a code: uppercase, bold, letter-spaced. Only FAIL gets a fill, so a
  column of passes reads as a quiet list and the failure is the one thing that stands out.
*/
const OUTCOME =
  'inline-flex h-5 items-center gap-1 rounded-sm px-1.5 text-micro font-bold tracking-wide whitespace-nowrap';

export function OutcomeBadge({ outcome }: { outcome: RunOutcome | CheckOutcome }) {
  if (outcome === 'PASS') {
    return (
      <span className={`${OUTCOME} text-status-ok-ink`}>
        <Check size={CHIP_ICON} /> PASS
      </span>
    );
  }
  if (outcome === 'FAIL') {
    return (
      <span className={`${OUTCOME} bg-status-failed-soft text-status-failed-ink`}>
        <Cross size={CHIP_ICON} /> FAIL
      </span>
    );
  }
  return (
    <span className={`${OUTCOME} text-status-none-ink`}>
      <MinusCircle size={CHIP_ICON} /> NOT RUN
    </span>
  );
}

export type TagTone = 'neutral' | 'brand' | 'warning' | 'danger' | 'success';

const TAG_TONE: Record<TagTone, string> = {
  neutral: 'bg-surface-3 text-ink-2',
  brand: 'bg-brand-soft text-brand',
  warning: 'bg-status-impaired-soft text-status-impaired-ink',
  danger: 'bg-status-failed-soft text-status-failed-ink',
  success: 'bg-status-ok-soft text-status-ok-ink',
};

/** A small label in the chip geometry: neutral by default, toned only when it is a warning or a result. */
export function Tag({
  tone = 'neutral',
  icon,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: TagTone; icon?: ReactNode }) {
  return (
    <span className={[CHIP, TAG_TONE[tone], className].filter(Boolean).join(' ')} {...rest}>
      {icon}
      {children}
    </span>
  );
}

/* Proposal states are tags: neutral while waiting or done, brand while publishing, toned only for the result. */
const PROPOSAL_STYLE: Record<ProposalStatus, { tone: TagTone; icon: ReactNode }> = {
  open: { tone: 'neutral', icon: <Clock size={CHIP_ICON} /> },
  approved: { tone: 'brand', icon: <Refresh size={CHIP_ICON} /> },
  rejected: { tone: 'neutral', icon: <Cross size={CHIP_ICON} /> },
  superseded: { tone: 'neutral', icon: <MinusCircle size={CHIP_ICON} /> },
  withdrawn: { tone: 'neutral', icon: <MinusCircle size={CHIP_ICON} /> },
  published: { tone: 'success', icon: <CheckCircle size={CHIP_ICON} /> },
  publish_failed: { tone: 'danger', icon: <Warning size={CHIP_ICON} /> },
};

export function ProposalStatusBadge({
  status,
  className,
}: {
  status: ProposalStatus;
  className?: string;
}) {
  const style = PROPOSAL_STYLE[status];
  return (
    <Tag tone={style.tone} icon={style.icon} className={className}>
      {PROPOSAL_STATUS_LABEL[status]}
    </Tag>
  );
}

export function BlindBadge({ className }: { className?: string }) {
  return (
    <Tag icon={<EyeOff size={CHIP_ICON} />} className={className}>
      Blind
    </Tag>
  );
}

export function CountPill({
  count,
  tone = 'neutral',
  className,
}: {
  count: number;
  tone?: 'neutral' | 'attention';
  className?: string;
}) {
  // A count that changes pops once; the first render does not.
  const changes = useChangeCount(count);
  if (count <= 0) return null;
  return (
    <span
      key={changes}
      className={[
        'inline-flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1.5 text-micro font-bold tabular',
        // Attention is a soft brand fill: it says "needs you" without matching the active tab's blue.
        tone === 'attention' ? 'bg-brand-soft text-brand' : 'bg-surface-3 text-ink-2',
        changes > 0 ? 'animate-pop' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {count}
    </span>
  );
}
